import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import useScript from 'react-script-hook/lib/use-script';
import { Link } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import useLocaleNavigate from 'utils/customHooks/useLocaleNavigate';
//translations
import { useTranslation } from 'react-i18next';
// Zustand stores
import { useCartStore, useCompanyStore } from 'stores';
import { useUserStore } from 'stores/userStore';
// Utils
import { calculateShippingFee, calculateShippingCosts } from 'components/cart/utils';
// CONSTANTS
import { responsive } from '../../constants';
//API
import { getRelatedProduct } from 'api/products';
import { getValidateCode } from 'api/invoices';
//components
import ItemList from 'components/itemList/item';
import Items from './items';
import OrderSummary from 'components/cart/OrderSummary';
import CheckoutLayout from 'components/checkout/CheckoutLayout';
import CheckoutStepper from 'components/checkout/CheckoutStepper';
import CheckoutHeader from 'components/checkout/CheckoutHeader';
import CheckoutNav from 'components/checkout/CheckoutNav';
import CheckoutHelpBox from 'components/checkout/CheckoutHelpBox';
import { goUp } from 'helpers';
import emptyBag from 'assets/img/shopping_bag.png';
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import './cart.scss';

const Cart = () => {
  const { t } = useTranslation(['cart']);
  const discountCodeRef = useRef();
  const previousValueRef = useRef('');
  const navigate = useLocaleNavigate();
  const {
    countItems,
    total,
    items,
    discountCart,
    totalWithDiscount,
    setIsGiftOrder,
    setAppliedCoupon,
    setDiscountCart,
    setErrorAmountPrice,
    setShippingFee,
  } = useCartStore();
  const {
    name,
    paypal_sandbox,
    paypal_production,
    shipping_cost,
    shipping_cost_islands,
    min_purchase_for_free_shipping,
  } = useCompanyStore();
  const { shippingAddress, isConcourseUser } = useUserStore();
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { assignedLibrary } = useAssignedLibrary();
  const [isDiscoungBeingApplied, setIsDiscoungBeingApplied] = useState(false);
  const [error, setError] = useState({ status: false, message: '' });
  const CLIENT = {
    sandbox: paypal_sandbox,
    production: paypal_production,
  };
  const CLIENT_ID = process.env.NODE_ENV === 'production' ? CLIENT.production : CLIENT.sandbox;
  useScript({
    src: `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}&currency=EUR`,
  });
  const checkDiscount = async () => {
    setIsDiscoungBeingApplied(true);
    setErrorAmountPrice(false);
    if (discountCodeRef.current.value !== '') {
      try {
        const { status, data } = await getValidateCode(discountCodeRef.current.value);
        if (status === 200) {
          if (data.valid) {
            setAppliedCoupon(discountCodeRef.current.value);
            const { discount_amount, discount_percent, type_discount } = data.promo_code;
            if (discount_percent > 0) {
              setDiscountCart({
                type: 'percentage',
                quantity: discount_percent,
                has_discount: true,
              });
            } else if (type_discount === 'gift_card') {
              setDiscountCart({
                type: type_discount,
                quantity: discount_amount,
                has_discount: true,
                code: discountCodeRef.current.value,
              });
            } else {
              setDiscountCart({
                type: '',
                quantity: 0,
                has_discount: false,
              });
              throw new Error('El valor del descuento debería ser mayor de 0.');
            }
          } else {
            setError({ status: true, message: 'errorApplyCode' });
          }
        }
      } catch (err) {
        console.log(err);
      }
    }
    setIsDiscoungBeingApplied(false);
  };

  const handleRelatedProducts = async () => {
    if (items[items?.length - 1] !== undefined) {
      const itemId = items[items?.length - 1]?.id;
      const fields = ['id', 'title', 'url', 'image', 'authors', 'type', 'price', 'discount', 'has_stock', 'iva'].join(',');
      const { status, data } = await getRelatedProduct(itemId, { fields });
      if (status === 200) {
        setRelatedProducts(data);
      }
    }
  };

  useEffect(() => {
    window.document.title = 'Carrito de compras | ' + name;
    goUp();
  }, []);

  useEffect(() => {
    total && handleRelatedProducts();
  }, [total]);


  // Recalcular envío cuando cambia el total o la dirección, aplicando envío gratuito
  useEffect(() => {
    if (items && items.length > 0 && total) {
      const province = shippingAddress?.province?.label || shippingAddress?.province || '';
      const baseShippingCost = calculateShippingFee(province, items, shipping_cost, shipping_cost_islands);
      const finalShippingCost = calculateShippingCosts(
        baseShippingCost,
        min_purchase_for_free_shipping || 0,
        total,
        isConcourseUser
      );
      setShippingFee(finalShippingCost);
    }
  }, [
    total,
    items,
    shippingAddress?.province,
    shipping_cost,
    shipping_cost_islands,
    min_purchase_for_free_shipping,
    setShippingFee,
    isConcourseUser,
  ]);

  const controlChangesDiscountCode = (e) => {
    setError({ status: false, message: '' });
    const currentValue = e.target.value;
    if (previousValueRef.current !== currentValue) {
      setDiscountCart({
        type: '',
        quantity: 0,
        has_discount: false,
      });
    }
    previousValueRef.current = currentValue;
    discountCodeRef.current.value = currentValue;
  };

  const isGiftCard = discountCart?.type === 'gift_card';
  const shouldDisableButton = isDiscoungBeingApplied || (isGiftCard && totalWithDiscount <= 0);

  const goToShipping = useCallback(() => {
    if (!shouldDisableButton) {
      setIsGiftOrder(false);
      navigate(`/${assignedLibrary}/envio`);
      goUp();
    }
  }, [assignedLibrary, navigate, setIsGiftOrder, shouldDisableButton]);

  if (!countItems) {
    return (
      <div className="checkout-page">
        <div className="row noFavorites text-center justify-content-center py-5">
          <div className="col-lg-10 margin-bottom-md mt-5">
            <img src={emptyBag} alt="bag.png" />
          </div>
          <div className="col-lg-10 margin-bottom-md">
            <h2>{t('emptyCart')}</h2>
            <p>{t('continueShopping')}</p>
            <p>
              {t('goBackCatalog')}
              <Link to="/"> {name}</Link>
            </p>
            <Link to="/">
              <Button className="checkout-btn checkout-btn--primary mt-4 p-3">{t('goBackHome')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <CheckoutLayout sidebar={total > 0 && <OrderSummary activeStep={0} />}>
        <CheckoutStepper activeStep={0} />
        <CheckoutHeader step={1} title={t('yourCartTitle')} />
        <Items isResume={false} />

        <div className="checkout-discount">
          <p className="checkout-discount__label">{t('tienesUnCodigo')}</p>
          <div className="checkout-discount__row">
            <Form.Control ref={discountCodeRef} onChange={controlChangesDiscountCode} />
            <Button className="blue-btn" onClick={checkDiscount} disabled={discountCart?.has_discount}>
              {t('aplicar')}
            </Button>
          </div>
          {error.status && <small className="text-danger d-block mt-2">{t(error.message)}</small>}
          <p className="checkout-discount__warning">{t('promoCodeWarning')}</p>
        </div>

        <CheckoutNav
          nextButton={
            <Button
              onClick={goToShipping}
              className="checkout-btn checkout-btn--primary"
              disabled={shouldDisableButton}
            >
              {t('following')} →
            </Button>
          }
        />

        <CheckoutHelpBox />
      </CheckoutLayout>

      {relatedProducts?.length > 0 && (
        <div className="checkout-page">
          <div className="checkout-page__container container-fluid">
            <section className="margin-bottom-md list-section">
              <h3>{t('relatedProducts')}</h3>
              <Carousel
                responsive={responsive}
                slidesToSlide={1}
                minimumTouchDrag={40}
                draggable
                infinite
                centerMode={false}
                swipeable
              >
                {relatedProducts.map((item, index) => (
                  <div key={`relatedProduct#${index}`} className="slide-div">
                    <ItemList data={item} />
                  </div>
                ))}
              </Carousel>
            </section>
          </div>
        </div>
      )}
    </>
  );
};

export default Cart;
